import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { ShieldCheck, Wrench, Sparkles, Scissors, Shield, Cpu, ArrowUpRight, Phone } from "lucide-react";
import { Reveal } from "../components/Reveal";

const GROUPS = [
  {
    n: "01",
    label: "Preventive & General",
    icon: ShieldCheck,
    items: [
      { title: "Oral Hygiene Care", body: "Good daily habits — brushing twice a day, flossing, a balanced diet and fluoride — stop problems before they start. Regular checkups and replacing your toothbrush every 3–4 months keep teeth clean, gums pink, and breath fresh." },
      { title: "Dental Cleaning", body: "A professional cleaning (about 30 minutes) recommended every six months. We remove plaque buildup and polish your teeth to prevent gum disease and decay." },
      { title: "Sealants", body: "A protective coating brushed onto the deep grooves and pits of teeth that are too narrow to clean by brushing — sealing out plaque and preventing cavities over time." },
    ],
  },
  {
    n: "02",
    label: "Restorative",
    icon: Wrench,
    items: [
      { title: "Fillings", body: "Restore a tooth damaged by decay to its normal shape and function. We remove the decay and fill the cavity — with gold, composite or porcelain — to seal out bacteria and prevent further decay." },
      { title: "Bonding", body: "Tooth-colored composite resin adhered to the front of a tooth to repair decay, reshape it, close gaps or improve its look — then shaped and polished for a natural finish." },
      { title: "Non-Surgical Gum Treatment", body: "For unhealthy gums (redness, bleeding, bad breath, receding gums), a deep cleaning or root planing under local anesthetic — with antibiotics — to restore gum health and help avoid surgery." },
      { title: "Crowns & Bridges", body: "Crowns (“caps”) cover and strengthen a damaged tooth; bridges replace missing teeth using neighboring teeth for support. Custom-made to match your smile, they restore function and keep teeth from shifting." },
      { title: "Root Canal Treatment", body: "Cleans out infected tooth pulp and disinfects the canals to save a tooth with deep decay, damage or an abscess. The tooth is sealed and usually restored with a crown." },
      { title: "Dental Implants", body: "A titanium post Dr. Palmisano places in the jaw to permanently replace missing teeth. Strong, long-lasting and independent of neighboring teeth — ideal with healthy gums and good hygiene." },
      { title: "Dentures", body: "Removable replacements for missing teeth — partial when some natural teeth remain, or complete for a full arch. Custom-made to look natural and restore eating, speaking and your smile." },
    ],
  },
  {
    n: "03",
    label: "Cosmetic",
    icon: Sparkles,
    items: [
      { title: "Teeth Whitening", body: "Safe, effective brightening — Zoom! in-office whitening for dramatic results in about an hour, or custom take-home trays to whiten at your own convenience." },
      { title: "Veneers", body: "Thin custom coverings placed over the visible surface of a tooth to improve its appearance — done directly with bonding or indirectly with lab-made porcelain, removing less tooth structure than a crown." },
      { title: "Lumineers", body: "Ultra-thin porcelain veneers applied without grinding or shaving the tooth. A painless, reversible path to a whiter, perfectly aligned smile in just 2–3 visits." },
    ],
  },
  {
    n: "04",
    label: "Oral Surgery",
    icon: Scissors,
    items: [
      { title: "Tooth Extractions", body: "When a tooth can't be saved — severe decay, infection, fracture or crowding — it's carefully removed under local anesthetic, with clear aftercare for proper healing." },
      { title: "Wisdom Tooth Extractions", body: "Removal of problematic third molars that are impacted, misaligned or crowded. A routine procedure under local anesthesia or sedation, with simple aftercare." },
    ],
  },
  {
    n: "05",
    label: "Appliances & TMJ",
    icon: Shield,
    items: [
      { title: "Nightguards & Mouthguards", body: "Custom flexible guards molded to your teeth — mouthguards to protect against sports impacts, nightguards to shield teeth from clenching and grinding while you sleep." },
      { title: "TMJ Treatments", body: "Care for jaw-joint problems that cause headaches, earaches, jaw pain or clicking. Options include bite adjustment, tooth alignment and a mouthpiece to stop clenching and grinding." },
    ],
  },
  {
    n: "06",
    label: "Technology & Comfort",
    icon: Cpu,
    items: [
      { title: "Digital X-Rays", body: "Digital radiography captures highly detailed images using up to 90% less radiation than film — helping us detect issues in teeth, bone and gums early." },
      { title: "Paperless Charting", body: "A fully digital office. X-rays and charts are recorded electronically for accurate, consistent, easy-to-read records that can be securely shared with your other providers." },
      { title: "Digital Imaging Software", body: "Lets us preview how a cosmetic or treatment procedure would change your smile, and documents your case with images of your face, teeth and smile." },
      { title: "Intraoral Imaging", body: "A pen-sized camera displays full-color, zoomed-in views of your teeth and gums on screen — catching cracks, chips and decay early, and helping you see what we see." },
      { title: "Electronic Claim Processing", body: "Insurance claims are submitted electronically for faster, more accurate processing — fewer errors and denials, quicker turnaround. We file on your behalf." },
      { title: "Telescopic Loupes", body: "Magnifying surgical glasses that let the dentist see fine details invisible to the naked eye, for more precise, careful treatment." },
    ],
  },
];

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <section className="relative z-10 min-h-screen pt-32 pb-24 px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C05C42] mb-4">Our Services</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter leading-none text-[#2A2E26] max-w-3xl">
            Complete dental care, <span className="italic text-[#C05C42]">explained simply</span>.
          </h1>
          <p className="mt-6 text-lg text-[#4A4E46] max-w-2xl leading-relaxed">
            From routine cleanings to cosmetic and restorative dentistry, here's a plain-language
            guide to the treatments and technology we offer. Have a question? We're always happy to talk it through.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="tel:5047330871" data-testid="services-call-btn"
              className="btn-terra font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2">
              <Phone size={16} /> Call to book · 504-733-0871
            </a>
            <a href="#contact" data-testid="services-contact-link"
              className="text-sm font-semibold text-[#2A2E26] hover:text-[#C05C42] transition-colors inline-flex items-center gap-1">
              Practice details <ArrowUpRight size={15} />
            </a>
          </div>
        </Reveal>

        <div className="mt-20 space-y-20">
          {GROUPS.map((g) => (
            <div key={g.label} id={`svc-${g.n}`} className="scroll-mt-28" data-testid={`service-group-${g.n}`}>
              <Reveal>
                <div className="flex items-end gap-5 border-b border-[#2A2E26]/10 pb-6 mb-10">
                  <span className="font-display text-6xl lg:text-7xl font-light text-[#C05C42]/25 leading-none">{g.n}</span>
                  <div className="flex items-center gap-3 pb-1">
                    <g.icon size={22} className="text-[#C05C42]" />
                    <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#2A2E26]">{g.label}</h2>
                  </div>
                </div>
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {g.items.map((item, i) => (
                  <Reveal key={item.title} delay={i * 0.05}>
                    <div className="h-full bg-[#F0EBE1] border border-[#2A2E26]/5 rounded-3xl p-7 hover:-translate-y-1 transition-transform duration-300">
                      <h3 className="font-display text-xl text-[#2A2E26] mb-3">{item.title}</h3>
                      <p className="text-sm text-[#4A4E46] leading-relaxed">{item.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Reveal className="mt-24 text-center bg-[#2A2E26] rounded-[32px] py-16 px-8">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#F9F6F0] mb-4">
            Ready to book your visit?
          </h2>
          <p className="text-[#C9D1C5] max-w-lg mx-auto mb-8">
            New patients are always welcome. Call us and we'll find a time that works for your family.
          </p>
          <a href="tel:5047330871" data-testid="services-cta-call"
            className="btn-terra font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2">
            <Phone size={18} /> 504-733-0871
          </a>
        </Reveal>
      </div>
    </section>
  );
}
